package songwritersnotebook.app.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import songwritersnotebook.app.billing.BillingClientWrapper
import songwritersnotebook.app.billing.SubscriptionTier
import songwritersnotebook.app.data.ChordDefinition
import songwritersnotebook.app.data.Song
import songwritersnotebook.app.data.UserProfile
import songwritersnotebook.app.di.PlatformDependencyProvider
import songwritersnotebook.app.utils.*
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.auth.FirebaseUser
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.database.database
import kotlinx.coroutines.Dispatchers

import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import kotlinx.datetime.Clock

import songwritersnotebook.app.tutorial.TutorialManager
import songwritersnotebook.app.tutorial.TutorialStep
import songwritersnotebook.app.database.SongRepository
import songwritersnotebook.app.data.PublicUser
import songwritersnotebook.app.getPlatform

class MainViewModel : ViewModel() {
    private val preferencesManager = PreferencesManager()
    val billingClientWrapper = BillingClientWrapper(viewModelScope)

    val productDetailsState = billingClientWrapper.productDetailsState
    val activeSubscription = billingClientWrapper.activeSubscription
    val billingError = billingClientWrapper.billingError

    private val authHandler = AuthHandler()
    val currentUser: StateFlow<FirebaseUser?> = authHandler.authState.stateIn(viewModelScope, SharingStarted.Eagerly, authHandler.getCurrentUser())

    private val _userProfile = MutableStateFlow<UserProfile?>(null)
    val userProfile: StateFlow<UserProfile?> = _userProfile.asStateFlow()

    val currentSubscriptionTier: StateFlow<SubscriptionTier> = combine(
        billingClientWrapper.activeSubscription,
        _userProfile
    ) { activeSub, profile ->
        val profileTier = profile?.subscriptionTier
        if (activeSub == SubscriptionTier.STUDIO || activeSub == SubscriptionTier.HOBBYIST) {
            activeSub
        } else if (profileTier == "STUDIO") {
            SubscriptionTier.STUDIO
        } else if (profileTier == "HOBBYIST") {
            SubscriptionTier.HOBBYIST
        } else {
            activeSub
        }
    }.stateIn(viewModelScope, SharingStarted.Eagerly, SubscriptionTier.UNKNOWN)

    private val _databaseReady = MutableStateFlow(false)
    val databaseReady: StateFlow<Boolean> = _databaseReady.asStateFlow()

    private val db = Firebase.firestore
    
    // In KMP, we get repository from provider
    private var songRepository: SongRepository? = null
    private var chordLibraryRepository: songwritersnotebook.app.database.ChordLibraryRepository? = null
    var playIntegrityService: PlayIntegrityService? = null

    private val _chordLibrary = MutableStateFlow<Map<String, ChordDefinition>>(emptyMap())
    val chordLibrary: StateFlow<Map<String, ChordDefinition>> = _chordLibrary.asStateFlow()

    private val _favoriteChords = MutableStateFlow<List<songwritersnotebook.app.data.SavedChord>>(emptyList())
    val favoriteChords: StateFlow<List<songwritersnotebook.app.data.SavedChord>> = _favoriteChords.asStateFlow()

    private val _favoriteProgressions = MutableStateFlow<List<songwritersnotebook.app.data.SavedProgression>>(emptyList())
    val favoriteProgressions: StateFlow<List<songwritersnotebook.app.data.SavedProgression>> = _favoriteProgressions.asStateFlow()

    private val _currentSong = MutableStateFlow<Song?>(null)
    val orderedChordNames: StateFlow<List<String>> = 
        combine(_chordLibrary, _currentSong) { library, song ->
            deriveOrderedChordNames(library, song)
        }.stateIn(
            scope = viewModelScope,
            started = SharingStarted.WhileSubscribed(5000),
            initialValue = emptyList()
        )

    private val _soundLoadProgress = MutableStateFlow(1.0f)
    val soundLoadProgress: StateFlow<Float> = _soundLoadProgress.asStateFlow()

    private val _isSyncing = MutableStateFlow(false)
    val isSyncing: StateFlow<Boolean> = _isSyncing.asStateFlow()
    
    private var syncTimeoutJob: kotlinx.coroutines.Job? = null
    private var profileJob: kotlinx.coroutines.Job? = null

    fun updateIsSyncing(isSyncing: Boolean) {
        _isSyncing.value = isSyncing
        
        // Safety timeout to prevent infinite loading
        syncTimeoutJob?.cancel()
        if (isSyncing) {
            syncTimeoutJob = viewModelScope.launch {
                delay(15000) // 15 seconds timeout
                if (_isSyncing.value) {
                    println("MainViewModel: Syncing timeout reached. Forcing isSyncing = false")
                    _isSyncing.value = false
                }
            }
        }
    }

    val deviceId = preferencesManager.deviceId
    private val _isSessionLimitExceeded = MutableStateFlow(false)
    val isSessionLimitExceeded: StateFlow<Boolean> = _isSessionLimitExceeded.asStateFlow()

    // _showTutorial removed in favor of TutorialManager
    private val _showAnonymousMigrationPrompt = MutableStateFlow(false)
    val showAnonymousMigrationPrompt: StateFlow<Boolean> = _showAnonymousMigrationPrompt.asStateFlow()

    private val _messageBus = Channel<String>()
    val messageBus = _messageBus.receiveAsFlow()
    
    private val _isMaintenanceMode = MutableStateFlow(false)
    val isMaintenanceMode: StateFlow<Boolean> = _isMaintenanceMode.asStateFlow()
    
    val metronome = MetronomeController(viewModelScope)

    fun showMessage(message: String) {
        viewModelScope.launch {
            _messageBus.send(message)
        }
    }

    init {
        println("MainViewModel Initialized.")
        
        setupAppLifecycleObserver {
            // FORCE_RECOMPILE
            try {
                Firebase.database.goOffline()
                Firebase.database.goOnline()
                println("App woke up: Firebase Database connection reset.")
            } catch (e: Exception) {
                println("Failed to reset Firebase connection on wake: $e")
            }
        }
        
        viewModelScope.launch {
            try {
                val configManager = songwritersnotebook.app.utils.RemoteConfigManager()
                configManager.fetchAndActivate()
                _isMaintenanceMode.value = configManager.getBoolean("is_maintenance_mode")
            } catch (e: Exception) {
                println("Failed to fetch remote config: $e")
            }
        }
        
        viewModelScope.launch {
            // Poll for database availability (or we could use a flow if PlatformDependencyProvider was flow-based)
            // For now, poll until initialized by main.kt or SongwritersApp
            val provider = PlatformDependencyProvider()
            while (provider.getSongRepository() == null) {
                delay(100)
            }
            
            songRepository = provider.getSongRepository()
            chordLibraryRepository = provider.getChordLibraryRepository()
            playIntegrityService = provider.getPlayIntegrityService()
            
            // PROACTIVE RHYME INIT: Start dictionary import/check immediately on app startup
            // so it's ready by the time the user opens a song.
            val currentDb = provider.getDatabase()
            if (currentDb != null) {
                songwritersnotebook.app.data.RhymeRepository.initialize(currentDb)
            }
            
            // Link favorite chords flow
            chordLibraryRepository?.favoriteChords?.onEach { _favoriteChords.value = it }?.launchIn(viewModelScope)
            chordLibraryRepository?.favoriteProgressions?.onEach { _favoriteProgressions.value = it }?.launchIn(viewModelScope)
            
            _databaseReady.value = true
            println("MainViewModel: Database components initialized.")

            // Tutorial trigger moved to onAuthStateChanged to ensure users sign in first
            
            // Start Auth listener once repo ready
            combine(currentUser, authHandler.isAuthLoading) { user, isLoading ->
                Pair(user, isLoading)
            }.collect { (user, isLoading) ->
                if (!isLoading) {
                    onAuthStateChanged(user)
                }
            }
        }

        // Cache samples
        viewModelScope.launch(songwritersnotebook.app.AppDispatchers.IO) {
            val instruments = listOf("piano", "guitar", "bass", "drums")
            _soundLoadProgress.value = 0.0f
            AudioSampleLoader.cacheAllSamples(instruments) { progress ->
                _soundLoadProgress.value = progress
            }
            _soundLoadProgress.value = 1.0f
        }

        viewModelScope.launch {
            billingClientWrapper.consumablePurchase.collect { credits: Int ->
                awardAiCredits(credits)
            }
        }

        viewModelScope.launch {
            activeSubscription.collect { newTier ->
                val user = currentUser.value
                if (user == null) return@collect

                val currentTierString = _userProfile.value?.subscriptionTier ?: "FREE"
                
                // Update Ad Manager premium status
                val isPaid = newTier != SubscriptionTier.FREE && newTier != SubscriptionTier.UNKNOWN
                InterstitialAdManager.updatePremiumStatus(isPaid)
                
                // Only trigger the "initial grant" if they are UPGRADING from FREE or UNKNOWN
                // This prevents the 'reset to 200 on every launch' bug.
                if (newTier != SubscriptionTier.FREE && newTier != SubscriptionTier.UNKNOWN) {
                    if (currentTierString == "FREE" || currentTierString == "UNKNOWN") {
                        println("DEBUG_VM: Tier upgrade detected (${currentTierString} -> ${newTier.name}). Granting credits and syncing data...")
                        CloudFunctionService.grantInitialMonthlyCredits(newTier.name)
                        
                        // PROACTIVE SYNC: Scan and upload all existing local data immediately
                        viewModelScope.launch {
                            songRepository?.uploadMissingRecordings()
                            songRepository?.syncSongsWithFirestore {}
                        }
                    }
                }

                // Sync profile logic
                if (newTier != SubscriptionTier.UNKNOWN) {
                     val isWeb = songwritersnotebook.app.getPlatform().name.contains("Web", ignoreCase = true)
                     
                     // SPECIAL CASE FOR WEB: Never push tier updates from the client.
                     // The server-side triggers (onStripeSubscriptionUpdate, syncUserTier) 
                     // handle Stripe-to-Profile sync automatically.
                     if (isWeb) {
                         return@collect
                     }

                     // Check if this is an UPGRADE relative to what Firestore currently thinks.
                     // We only push upgrades from the client to immediately enable features.
                     // Downgrades are handled by server-side sync logic (syncUserTier) to prevent
                     // transient client-side state (e.g. during billing initialization) from wiping 
                     // collaborations via Firestore triggers.
                     val isUpgrade = when (newTier) {
                         SubscriptionTier.STUDIO -> currentTierString != "STUDIO"
                         SubscriptionTier.HOBBYIST -> currentTierString == "FREE" || currentTierString == "UNKNOWN"
                         else -> false
                     }

                     if (isUpgrade) {
                         try {
                             val userRef = db.collection("users").document(user.uid)
                             // 10 GB for STUDIO, 0 for others (Hobbyist/Free)
                             val quotaLong = if (newTier == SubscriptionTier.STUDIO) 10737418240L else 0L
                             val isJs = getPlatform().name.contains("Web", ignoreCase = true)
                             val quota: Any = if (isJs) quotaLong.toDouble() else quotaLong
                             
                             userRef.update(
                                 "subscriptionTier" to newTier.name,
                                 "dataStorageQuotaBytes" to quota
                             )
                             println("Updated Firestore tier to ${newTier.name} (Client-side Upgrade)")
                         } catch (e: Exception) {
                             println("Failed to update tier in Firestore: $e")
                         }
                     }
                }
            }
        }
        
        // Monitor Tutorial Completion
        viewModelScope.launch {
            var wasActive = false
            TutorialManager.currentStep.collect { step ->
                if (step != TutorialStep.NONE) {
                    wasActive = true
                } else if (wasActive) {
                    // Tutorial just finished
                    preferencesManager.hasSeenTutorial = true
                    songRepository?.updateUserProfileFields(mapOf("hasSeenTutorial" to true))
                    wasActive = false
                }
            }
        }
    }

    private fun onAuthStateChanged(user: FirebaseUser?) {
        viewModelScope.launch {
            if (user == null) {
                _userProfile.value = null
                songRepository?.switchUser(null, null)
                chordLibraryRepository?.startSync(null)
            } else {
                if (songRepository?.hasAnonymousSongs() == true) {
                    _showAnonymousMigrationPrompt.value = true
                } else {
                    val fs = songwritersnotebook.platform.getPlatformFileSystem()
                    val dir = fs.getAppFilesDirectory()
                    val anonFile = fs.getFile(dir, "anonymous_songs.json")
                    if (anonFile.exists()) anonFile.delete()
                    
                    signInUser(user)
                }
            }
        }
    }

    fun mergeAnonymousSongs() {
        val user = currentUser.value ?: return
        viewModelScope.launch(songwritersnotebook.app.AppDispatchers.IO) {
            _showAnonymousMigrationPrompt.value = false
            songRepository?.switchUser(null, user.uid)
            songRepository?.mergeAnonymousSongs(user.uid)
            signInUser(user)
        }
    }

    fun declineAnonymousSongs() {
        val user = currentUser.value ?: return
        viewModelScope.launch {
            _showAnonymousMigrationPrompt.value = false
            songRepository?.deleteAnonymousSongs()
            
            val fs = songwritersnotebook.platform.getPlatformFileSystem()
            val dir = fs.getAppFilesDirectory()
            val anonFile = fs.getFile(dir, "anonymous_songs.json")
            if (anonFile.exists()) anonFile.delete()
            signInUser(user)
        }
    }

    private fun signInUser(user: FirebaseUser) {
        viewModelScope.launch {
            updateIsSyncing(true)
            try {
                // user.getIdToken(false) // KMP Auth might not expose forceRefresh easily?
                // GitLive Auth: user.getIdToken(false) works
                // user.getIdToken(false) 
                
                songRepository?.switchUser(null, user.uid)
                chordLibraryRepository?.startSync(user.uid)
                listenToUserProfile(user.uid, user.email)
                billingClientWrapper.reinitialize()
                
                // Session Registration
                val platform = songwritersnotebook.app.getPlatform()
                val platformName = platform.name
                val deviceName = platform.deviceName

                songRepository?.registerSession(deviceId, deviceName, platformName)
                
                // Trigger background recovery of failed recording uploads
                songRepository?.uploadMissingRecordings()

                // Trigger tutorial if not seen yet
                val isWeb = songwritersnotebook.app.getPlatform().name.contains("Web", ignoreCase = true)
                if (!preferencesManager.hasSeenTutorial && !isWeb) {
                    requestTutorial()
                }

                println("DEBUG_FIRESTORE: MainViewModel.signInUser triggering syncSongsWithFirestore")
                songRepository?.syncSongsWithFirestore {
                    if (preferencesManager.downloadAllAudio) {
                        viewModelScope.launch {
                            val repo = songRepository
                            repo?.syncAllRecordings { msg ->
                                if (msg.isBlank()) {
                                    updateIsSyncing(false)
                                } else {
                                    updateIsSyncing(true)
                                    showMessage(msg)
                                }
                            }
                        }
                    } else {
                        updateIsSyncing(false)
                    }
                }
            } catch (e: Exception) {
                println("Error signing in: $e")
                updateIsSyncing(false)
                showMessage("Error signing in.")
                authHandler.signOut()
            }
        }
    }

    fun awardAiCredits(amount: Int) {
        viewModelScope.launch {
            val user = currentUser.value
            if (user != null) {
                CreditService.addOneTimeCredits(user.uid, amount)
            }
        }
    }

    suspend fun useAiCredits(amount: Int): Boolean {
        val user = currentUser.value ?: return false
        return CreditService.useAiCredits(user.uid, amount)
    }

    fun onTutorialFinished() {
        preferencesManager.hasSeenTutorial = true
        TutorialManager.stopTutorial()
    }

    fun clearBillingError() {
        billingClientWrapper.clearError()
    }

    fun restorePurchases() {
        viewModelScope.launch {
            billingClientWrapper.queryActivePurchases(currentUser.value?.uid)
            showMessage("Checking for existing purchases...")
        }
    }
    
    fun refreshUserProfile() {
        val user = currentUser.value ?: return
        val userDocRef = db.collection("users").document(user.uid)
        viewModelScope.launch {
            try {
                val snapshot = userDocRef.get()
                if (snapshot.exists) {
                    val profile: UserProfile = snapshot.data()
                    println("DEBUG_VM: Refreshed Profile - UsedBytes: ${profile.dataStorageUsedBytes}, Quota: ${profile.dataStorageQuotaBytes}")
                    _userProfile.value = profile
                    songRepository?.updateUserProfile(profile)
                }
            } catch (e: Exception) {
                println("Error refreshing profile: $e")
            }
        }
    }

    private fun listenToUserProfile(uid: String, email: String?) {
        val userDocRef = db.collection("users").document(uid)
        
        profileJob?.cancel()
        profileJob = viewModelScope.launch {
            try {
                userDocRef.snapshots.collect { snapshot ->
                    AppLogger.d("FIRESTORE_USAGE", "READ: userDocRef.snapshots fired for uid $uid")
                    println("DEBUG: UserProfile snapshot received. Exists: ${snapshot.exists}")
                    if (snapshot.exists) {
                        try {
                            val profile: UserProfile = snapshot.data()
                            val previousTierString = _userProfile.value?.subscriptionTier ?: "FREE"
                            val previousTier = try {
                                SubscriptionTier.valueOf(previousTierString)
                            } catch (e: Exception) { SubscriptionTier.FREE }
                            
                            println("DEBUG: Firestore Profile - Tier: ${profile.subscriptionTier}, Quota: ${profile.dataStorageQuotaBytes}, DailyCredits: ${profile.aiCreditsDaily}, OneTimeCredits: ${profile.aiCreditsOneTime}")
                            _userProfile.value = profile
                            songRepository?.updateUserProfile(profile)
                            
                            // Sync device preferences if the profile says we've already seen them
                            if (profile.hasSeenTutorial) {
                                preferencesManager.hasSeenTutorial = true
                            }
                            if (profile.hasPromptedForNotifications) {
                                preferencesManager.hasPromptedForNotifications = true
                            }
                            
                            // Session Limit Check (Limit 3)
                            val sessions = profile.activeSessions
                            if (sessions.size > 3) {
                                _isSessionLimitExceeded.value = true
                            }
                            // We do NOT set it to false here automatically, 
                            // to allow the user to click "Done" manually.

                            // Check tier
                            val tier = try {
                                SubscriptionTier.valueOf(profile.subscriptionTier)
                            } catch (e: Exception) { SubscriptionTier.FREE }
                            
                            billingClientWrapper.forceUpdateSubscriptionTier(tier)

                            // PRO UX: Force Token Refresh on Tier Change
                            // If the tier changed to a paid one, force an ID Token refresh 
                            // to update Custom Claims (like 'studio') instantly.
                            if (tier != previousTier && tier != SubscriptionTier.FREE && tier != SubscriptionTier.UNKNOWN) {
                                println("DEBUG: Tier upgrade detected. Forcing Auth Token refresh...")
                                viewModelScope.launch {
                                    try {
                                        dev.gitlive.firebase.Firebase.auth.currentUser?.getIdToken(true)
                                        println("DEBUG: Auth Token refreshed successfully.")
                                    } catch (e: Exception) {
                                        println("DEBUG: Failed to refresh Auth Token: $e")
                                    }
                                }
                            }
                            
                        } catch (e: Exception) {
                            println("Error parsing profile: $e")
                        }
                    } else {
                        println("DEBUG: Profile does not exist. Creating free profile.")
                        // Create profile if missing
                        val freeProfile = UserProfile(email = email ?: "")
                        userDocRef.set(freeProfile)
                    }
                }
            } catch (e: Exception) {
                println("Profile listener error: $e")
            }
        }
    }

    fun onGoogleSignInResult(credentials: AuthCredentials) {
        viewModelScope.launch {
            updateIsSyncing(true)
            val user = authHandler.firebaseAuthWithGoogle(credentials)
            if (user == null) {
                updateIsSyncing(false)
                showMessage("Sign in failed.")
            }
        }
    }

    suspend fun signInWithEmail(email: String, password: String) {
        updateIsSyncing(true)
        try {
            val user = authHandler.signInWithEmail(email, password)
            if (user == null) {
                updateIsSyncing(false)
                showMessage("Sign in failed.")
            } else if (currentUser.value?.uid == user.uid) {
                // If stateflow already has this user from a timed-out attempt, force re-initialization
                signInUser(user)
            }
        } catch (e: Throwable) {
            updateIsSyncing(false)
            val msg = e.message ?: "Sign in failed."
            println("VM: Sign in error: $msg")
            throw Exception(msg)
        }
    }

    suspend fun signUpWithEmail(email: String, password: String) {
        updateIsSyncing(true)
        try {
            val user = authHandler.signUpWithEmail(email, password)
            if (user == null) {
                updateIsSyncing(false)
                showMessage("Account creation failed.")
            } else if (currentUser.value?.uid == user.uid) {
                signInUser(user)
            }
        } catch (e: Throwable) {
            updateIsSyncing(false)
            val msg = e.message ?: "Account creation failed."
            println("VM: Signup error: $msg")
            throw Exception(msg)
        }
    }

    suspend fun sendPasswordResetEmail(email: String) {
        try {
            authHandler.sendPasswordResetEmail(email)
            showMessage("Password reset email sent.")
        } catch (e: Exception) {
            val msg = e.message ?: "Failed to send reset email."
            throw Exception(msg)
        }
    }

    fun signOut() {
        viewModelScope.launch {
            authHandler.signOut()
            billingClientWrapper.reset()
        }
    }

    private fun deriveOrderedChordNames(
        library: Map<String, ChordDefinition>,
        song: Song?
    ): List<String> {
        if (song == null) return library.keys.sorted()
        val orderedChords = LinkedHashSet<String>()
        val chordRegex = Regex("\\[([^\\]]+)\\]")

        song.sections.forEach { section ->
            section.lines.forEach { line ->
                chordRegex.findAll(line.text).forEach { match ->
                    val chordName = match.groupValues[1]
                    if (library.containsKey(chordName)) {
                        orderedChords.add(chordName)
                    }
                }
            }
        }
        library.keys.sorted().forEach { chordName ->
            if (!orderedChords.contains(chordName)) {
                orderedChords.add(chordName)
            }
        }
        return orderedChords.toList()
    }

    fun updateSongBpm(songId: String, bpm: Int) {
        viewModelScope.launch(songwritersnotebook.app.AppDispatchers.IO) {
            val currentSong = songRepository?.getSongById(songId)
            if (currentSong != null) {
                val updatedSong = currentSong.copy(bpm = bpm)
                songRepository?.saveSongToAllSources(updatedSong, scope = songwritersnotebook.app.crdt.UndoScope.EDITOR)
            }
        }
    }

    fun loadLibraryFromSong(song: Song) {
        _currentSong.value = song
        _chordLibrary.value = song.chordDefinitions
    }

    fun playNotePreview(noteName: String, instrument: String, volume: Float = 0.8f) {
        // Use the singleton AudioHandler for previews.
        val audioHandler = songwritersnotebook.app.di.PlatformDependencyProvider().getAudioHandler()
        audioHandler.playNote(noteName, instrument)
    }

    fun playChordPreview(
        chordName: String,
        currentUiInstrument: String,
        strumSpeed: Float = 0.5f,
        volume: Float = 0.8f
    ) {
       // Similar to old logic but using coroutines and playNotePreview
       val chordDef = _chordLibrary.value[chordName]
       val notesToPlay: List<String> = if (currentUiInstrument.equals("guitar", ignoreCase = true)) {
           val song = _currentSong.value ?: songwritersnotebook.app.data.Song.createEmpty()
           val tab = chordDef?.guitarTab?.ifEmpty { null }?.map { it.toIntOrNull() ?: if (it == "x") 'x' else 0 }
               ?: songwritersnotebook.app.utils.ChordUtils.getTabForCapo(songwritersnotebook.app.utils.ChordUtils.parseChordName(chordName).first, songwritersnotebook.app.utils.ChordUtils.parseChordName(chordName).second, song.guitarCapo)
               ?: songwritersnotebook.app.utils.ChordUtils.GUITAR_CHORD_TABS[chordName]
           if (tab != null) {
               songwritersnotebook.app.utils.ChordUtils.tabToNotes(tab, song.guitarTuning, song.guitarCapo)
           } else emptyList()
       } else {
           chordDef?.pianoNotes?.ifEmpty { null } ?: run {
               val (root, quality) = songwritersnotebook.app.utils.ChordUtils.parseChordName(chordName)
               songwritersnotebook.app.utils.ChordUtils.PIANO_CHORD_VOICINGS[chordName]
                   ?: songwritersnotebook.app.utils.ChordUtils.calculateChordNotes(root, quality)
           }
       }
       
       if (notesToPlay.isEmpty()) return
       
       val strumDelayMs = (strumSpeed * 120f).toLong().coerceAtLeast(10L)
       playNotesAsStrum(notesToPlay, currentUiInstrument, strumDelayMs, volume)
    }

    fun playNotesAsStrum(
        notes: List<String>,
        instrument: String,
        strumDelayMs: Long,
        volume: Float = 0.8f
    ) {
        val actualNotes = if ((notes as Any) is String) {
            (notes as Any as String).split(",").map { it.trim() }
        } else {
            notes
        }
        if (actualNotes.isEmpty()) return
        playNotePreview(actualNotes.first(), instrument, volume)
        if (actualNotes.size > 1) {
            viewModelScope.launch {
                if (strumDelayMs > 0) delay(strumDelayMs)
                actualNotes.drop(1).forEach { note ->
                    playNotePreview(note, instrument, volume)
                    if (strumDelayMs > 0) delay(strumDelayMs)
                }
            }
        }
    }

    fun addOrUpdateChord(songId: String, chordDef: ChordDefinition) {
        viewModelScope.launch(songwritersnotebook.app.AppDispatchers.IO) {
            val song = songRepository?.getSongById(songId) ?: return@launch
            val updatedDefinitions = song.chordDefinitions.toMutableMap().apply {
                this[chordDef.name] = chordDef
            }
            val updatedSong = song.copy(chordDefinitions = updatedDefinitions)
            songRepository?.saveSongToAllSources(updatedSong, scope = songwritersnotebook.app.crdt.UndoScope.EDITOR)
            if (_currentSong.value?.id == songId) {
                _chordLibrary.value = updatedDefinitions
            }
        }
    }

    fun updateChord(songId: String, oldName: String, newDefinition: ChordDefinition) {
        viewModelScope.launch(songwritersnotebook.app.AppDispatchers.IO) {
            val song = songRepository?.getSongById(songId) ?: return@launch
            val regex = """\[${Regex.escape(oldName)}\]""".toRegex()
            val updatedSections = song.sections.map { section ->
                section.copy(lines = section.lines.map { line ->
                    line.copy(text = line.text.replace(regex, "[${newDefinition.name}]"))
                })
            }
            val updatedDefinitions = song.chordDefinitions.toMutableMap().apply {
                remove(oldName)
                this[newDefinition.name] = newDefinition
            }
            val updatedSong = song.copy(sections = updatedSections, chordDefinitions = updatedDefinitions)
            songRepository?.saveSongToAllSources(updatedSong, scope = songwritersnotebook.app.crdt.UndoScope.EDITOR)
            if (_currentSong.value?.id == songId) {
                _chordLibrary.value = updatedDefinitions
            }
        }
    }

    fun deleteChord(songId: String, chordName: String) {
        viewModelScope.launch(songwritersnotebook.app.AppDispatchers.IO) {
            val song = songRepository?.getSongById(songId) ?: return@launch
            val updatedDefinitions = song.chordDefinitions.toMutableMap().apply {
                remove(chordName)
            }
            val updatedSong = song.copy(chordDefinitions = updatedDefinitions)
            songRepository?.saveSongToAllSources(updatedSong, scope = songwritersnotebook.app.crdt.UndoScope.EDITOR)
            if (_currentSong.value?.id == songId) {
                _chordLibrary.value = updatedDefinitions
            }
        }
    }

    fun requestTutorial() {
        TutorialManager.startTutorial(songRepository)
    }

    fun onSongEdited() {
        preferencesManager.lastSongEditTimestamp = Clock.System.now().toEpochMilliseconds()
    }

    fun toggleFavoriteChord(chord: ChordDefinition, tuning: List<String>) {
        viewModelScope.launch {
            val tier = currentSubscriptionTier.value
            val isPaid = tier != SubscriptionTier.FREE && tier != SubscriptionTier.UNKNOWN
            chordLibraryRepository?.toggleFavorite(chord, tuning, isPaid)
        }
    }

    fun updateUsername(newUsername: String, onResult: (Boolean, String?) -> Unit) {
        viewModelScope.launch {
            try {
                songRepository?.updateUsername(newUsername)
                refreshUserProfile()
                onResult(true, null)
            } catch (e: Exception) {
                val msg = e.message?.replace("functions/already-exists: ", "") 
                             ?.replace("functions/invalid-argument: ", "")
                             ?: "Update failed."
                onResult(false, msg)
            }
        }
    }

    fun requestAccountDeletion(onSuccess: () -> Unit, onError: (String) -> Unit) {
        viewModelScope.launch {
            try {
                updateIsSyncing(true)
                CloudFunctionService.requestAccountDeletion()
                refreshUserProfile()
                onSuccess()
            } catch (e: Exception) {
                onError(e.message ?: "Failed to request deletion.")
            } finally {
                updateIsSyncing(false)
            }
        }
    }

    fun cancelAccountDeletion(onSuccess: () -> Unit, onError: (String) -> Unit) {
        viewModelScope.launch {
            try {
                updateIsSyncing(true)
                CloudFunctionService.cancelAccountDeletion()
                refreshUserProfile()
                onSuccess()
            } catch (e: Exception) {
                onError(e.message ?: "Failed to cancel deletion.")
            } finally {
                updateIsSyncing(false)
            }
        }
    }

    fun dismissSessionLimitDialog() {
        _isSessionLimitExceeded.value = false
    }

    suspend fun removeSession(deviceId: String) {
        songRepository?.removeSession(deviceId)
        refreshUserProfile()
    }

    // --- Safety & Blocking ---
    
    /**
     * Users who are currently interacting with the current user via shared songs or invitations.
     * This allows users to block/report even if they aren't on the Studio tier.
     */
    val contactCandidates: StateFlow<List<PublicUser>> = combine(
        songRepository?.songs ?: flowOf(emptyList()),
        songRepository?.sharedSongs ?: flowOf(emptyList()),
        songRepository?.invitations ?: flowOf(emptyList())
    ) { owned, shared, invites ->
        val candidates = mutableMapOf<String, String>() // UID to DisplayName (Email/Username)
        
        // 1. People collaborating on MY songs
        owned.forEach { song ->
            song.collaboratorEmails.forEach { (uid, email) ->
                candidates[uid] = email
            }
        }
        
        // 2. People whose songs I AM COLLABORATING ON (The Owners)
        shared.forEach { song ->
            if (song.ownerId.isNotEmpty()) {
                // We might not have the owner's email in collaboratorEmails, 
                // but we can at least use their UID.
                candidates[song.ownerId] = "Owner of \"${song.title}\""
            }
        }
        
        // 3. People who INVITED me
        invites.forEach { invite ->
            candidates[invite.ownerId] = invite.ownerEmail
        }
        
        candidates.map { (uid, name) -> PublicUser(uid = uid, username = name) }
            .sortedBy { it.username }
    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), emptyList())

    fun blockUser(uid: String, displayName: String) {
        viewModelScope.launch {
            try {
                updateIsSyncing(true)
                val currentProfile = _userProfile.value ?: return@launch
                
                // 1. Update Profile Block List
                val updatedBlocked = currentProfile.blockedUsers.toMutableMap().apply {
                    put(uid, displayName)
                }
                songRepository?.updateUserProfileFields(mapOf("blockedUsers" to updatedBlocked))
                
                // 2. Proactive Cleanup: Remove them from all songs I own
                songRepository?.removeAllCollaborationsForUser(uid)
                
                // 3. Proactive Cleanup: Leave all songs owned by them
                songRepository?.leaveAllCollaborationsFromUser(uid)
                
                refreshUserProfile()
                showMessage("User blocked.")
            } catch (e: Exception) {
                println("Failed to block user: ${e.message}")
                showMessage("Failed to block user.")
            } finally {
                updateIsSyncing(false)
            }
        }
    }

    fun reportUser(uid: String, displayName: String) {
        val platformUtils = PlatformUtils()
        platformUtils.sendEmail(
            to = "support@songwritersnotebook.app",
            subject = "UGC Report: User $uid",
            body = "Reporting User: $uid\nDisplay Name: $displayName\nReason: [Please specify why you are reporting this user]"
        )
        showMessage("Redirecting to email to report user...")
    }

    fun unblockUser(uid: String) {
        viewModelScope.launch {
            try {
                val currentProfile = _userProfile.value ?: return@launch
                val updatedBlocked = currentProfile.blockedUsers.toMutableMap().apply {
                    remove(uid)
                }
                songRepository?.updateUserProfileFields(mapOf("blockedUsers" to updatedBlocked))
                refreshUserProfile()
                showMessage("User unblocked.")
            } catch (e: Exception) {
                println("Failed to unblock user: ${e.message}")
                showMessage("Failed to unblock user.")
            }
        }
    }
}

